import * as React from "react"

import { cn } from "@/lib/utils"
import { type IconType, Icon } from '@src/components/icon/Icon';
import { cva } from 'class-variance-authority';

export interface InputProps {
  iconType?: IconType,
  placeholder?: string,
  variant?: "default" | "tag",
  size?: "sm" | "md" | "lg" | "tag",
  min?: number;
  max?: number;
  step?: number;
  className?: string,
  disabled?: boolean;
  type?: string,
  value?: string,
  isValid?: boolean;
  onChange?: (evt: React.ChangeEvent<HTMLInputElement>) => void,
  onBlur?: () => void;
  onKeyDown?: (evt: React.KeyboardEvent<HTMLInputElement>) => void;
}

const cx = cva(["flex h-9 w-full rounded-md border border-input bg-input px-3 py-1 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"], {
  variants: {
    variant: {
      default: "hover:cursor-pointer focus:cursor-text border-input bg-input text-input-fg placeholder:text-input-fg shadow-sm",
      tag: "hover:cursor-pointer focus:cursor-text border-0 bg-transparent text-input-fg placeholder-text-input-fg font-light px-2 py-1",
    },
    size: {
      tag: 'leading-5 text-sm h-7 w-auto',
      sm: "leading-5",
      md: "leading-6",
      lg: "leading-7",
    }
  },
  defaultVariants: {
    variant: "default",
    size: "md",
  },
})

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({
    className,
    type,
    variant='default',
    size='md',
    iconType,
    placeholder,
    onChange,
    isValid = true,
    disabled,
    ...props
  }, ref) => {
    return (
      <div className="relative">
        {iconType && <Icon type={iconType} className="absolute left-0 top-0 h-full w-9" />}
        <input
          type={type}
          className={cn([
            cx({ variant, size }),
            className,
            {
              'pl-8': iconType,
              'border-destructive': !isValid
            },
          ])}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          ref={ref}
          {...props}
        />
      </div>
    )
  }
)
Input.displayName = "Input"
